/**
 * Generic date formatting utilities.
 *
 * These helpers are not specific to any single API domain and can be
 * reused across any value-matching operation or future API that needs
 * epoch-to-formatted-string conversion.
 */

/** Default timezone for date formatting. Currently IST (India Standard Time) as the primary user base is in India. */
const DEFAULT_TIMEZONE = 'Asia/Kolkata';

/**
 * Creates DateTimeFormat options for the given timezone.
 *
 * @param timeZone - IANA timezone string (defaults to DEFAULT_TIMEZONE)
 * @returns Intl.DateTimeFormatOptions configured for the specified timezone
 */
function createDateFormatOptions(timeZone: string = DEFAULT_TIMEZONE): Intl.DateTimeFormatOptions {
  return {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    // Use `hourCycle: 'h23'` (00–23) rather than `hour12: false`. In some
    // engines/locales, `hour12: false` resolves to `h24` (1–24) and yields
    // "24" at midnight, which breaks downstream parsing.
    hourCycle: 'h23',
    timeZone,
  };
}

/** Pre-built formatter for the default timezone. Reused across all calls to avoid repeated object creation. */
const defaultDateFormatter = new Intl.DateTimeFormat('en-IN', createDateFormatOptions());

/**
 * Cache for formatted date strings keyed by epoch milliseconds.
 * Avoids redundant formatting when the same timestamp appears in multiple rows.
 */
const formattedDateCache = new Map<number, string>();

/** Maximum cache size to prevent unbounded memory growth */
const MAX_CACHE_SIZE = 1000;

/**
 * Formats parts from an Intl.DateTimeFormat into the expected output string.
 *
 * @param parts - Array of DateTimeFormatPart from formatToParts
 * @returns Formatted date string in "yyyy/MM/dd  HH:mm" format
 */
function partsToString(parts: Intl.DateTimeFormatPart[]): string {
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}/${get('month')}/${get('day')}  ${get('hour')}:${get('minute')}`;
}

/**
 * Formats an epoch millisecond timestamp to a localized date string.
 * Output format: "yyyy/MM/dd  HH:mm" — matches the old Java CampaignListBuilder.
 *
 * Results are memoized to optimize performance for large datasets.
 * Uses the default timezone (IST) unless a custom timezone is provided.
 *
 * @param epochMs - Epoch timestamp in milliseconds
 * @param timeZone - Optional IANA timezone string (defaults to IST / 'Asia/Kolkata')
 * @returns Formatted date string, or empty string if input is undefined/null
 */
export function formatEpochToIST(epochMs?: number, timeZone?: string): string {
  if (epochMs === undefined || epochMs === null) {
    return '';
  }

  // Use custom formatter if a non-default timezone is requested
  if (timeZone && timeZone !== DEFAULT_TIMEZONE) {
    const customFormatter = new Intl.DateTimeFormat('en-IN', createDateFormatOptions(timeZone));
    return partsToString(customFormatter.formatToParts(new Date(epochMs)));
  }

  // Check cache for default timezone formatting
  const cached = formattedDateCache.get(epochMs);
  if (cached !== undefined) {
    return cached;
  }

  const formatted = partsToString(defaultDateFormatter.formatToParts(new Date(epochMs)));

  // Evict oldest entries when cache exceeds limit
  if (formattedDateCache.size >= MAX_CACHE_SIZE) {
    const firstKey = formattedDateCache.keys().next().value;
    if (firstKey !== undefined) {
      formattedDateCache.delete(firstKey);
    }
  }
  formattedDateCache.set(epochMs, formatted);

  return formatted;
}

/**
 * Clears the formatted date cache. Useful for testing or when timezone settings change.
 */
export function clearDateCache(): void {
  formattedDateCache.clear();
}

// ============================================================================
// Timezone-aware epoch ↔ local date/time conversion
// ============================================================================

/**
 * Converts epoch milliseconds → local date/time parts for the marketplace timezone.
 * Used to populate form fields from API response epoch values.
 *
 * @param epochMs - Epoch timestamp in milliseconds
 * @param timeZone - IANA timezone string (defaults to 'Asia/Kolkata')
 * @returns Object with `date` (YYYY-MM-DD) and `time` (HH:mm) in the local timezone
 */
export function epochToLocalParts(
    epochMs: number,
    timeZone: string = DEFAULT_TIMEZONE,
): { date: string; time: string } {
    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    });
    const parts = formatter.formatToParts(new Date(epochMs));
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
    return {
        date: `${get('year')}-${get('month')}-${get('day')}`,
        time: `${get('hour')}:${get('minute')}`,
    };
}

/**
 * Returns the timezone offset (in ms) between UTC and `timeZone` at the given
 * UTC instant. Computed by re-formatting the instant in the target timezone
 * and treating those parts as if they were UTC.
 */
function offsetAtInstant(utcInstantMs: number, formatter: Intl.DateTimeFormat): number {
    const parts = formatter.formatToParts(new Date(utcInstantMs));
    const get = (type: string) => parts.find(p => p.type === type)?.value ?? '00';
    const localStr = `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get('second')}.000Z`;
    return new Date(localStr).getTime() - utcInstantMs;
}

/**
 * Converts local date/time strings → epoch milliseconds, interpreting values
 * in the marketplace timezone. Uses Intl.DateTimeFormat to dynamically compute
 * the timezone offset.
 *
 * Handles DST edges: the offset at `utcGuess` may differ from the offset at
 * the actual target instant if a DST transition lies between them. We compute
 * an initial estimate, then recompute the offset at that estimate and apply a
 * single correction. This converges for all standard one-hour DST shifts.
 *
 * @param dateStr - Date in YYYY-MM-DD format
 * @param timeStr - Time in HH:mm format (defaults to "00:00" if empty)
 * @param timeZone - IANA timezone string (defaults to 'Asia/Kolkata')
 * @returns Epoch milliseconds, or undefined if dateStr is empty/malformed
 */
export function localPartsToEpoch(
    dateStr: string,
    timeStr: string,
    timeZone: string = DEFAULT_TIMEZONE,
): number | undefined {
    if (!dateStr) return undefined;
    const time = timeStr || '00:00';

    // Step 1: Parse the date+time as if it were UTC to get a reference point.
    const utcGuess = new Date(`${dateStr}T${time}:00.000Z`).getTime();
    if (Number.isNaN(utcGuess)) return undefined;

    const formatter = new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    });

    // Step 2: Initial offset at utcGuess.
    const initialOffset = offsetAtInstant(utcGuess, formatter);

    // Step 3: Recompute offset at the candidate target instant. If a DST
    // transition lies between utcGuess and (utcGuess - initialOffset), the
    // corrected offset at the target instant differs and we must use it.
    const correctedOffset = offsetAtInstant(utcGuess - initialOffset, formatter);

    return utcGuess - correctedOffset;
}
